import React, { useState } from "react";
import { TrendingUp, TrendingDown, Calendar, ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from '../../../contexts/LanguageContext'; // Add this import

const PriceRadar = ({ priceData = [], userSubscriptions = [] }) => {
  const { t } = useTranslation(); // Add this hook
  const [viewMode, setViewMode] = useState("absolute");
  const [expandedMerchants, setExpandedMerchants] = useState({});

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const toggleMerchantExpansion = (merchantId) => {
    setExpandedMerchants(prev => ({
      ...prev,
      [merchantId]: !prev[merchantId]
    }));
  };

  const getUserPlan = (merchant) => {
    if (!userSubscriptions || userSubscriptions.length === 0) return null;
    
    const merchantId = merchant.merchant_id.toString();
    
    return userSubscriptions.find(sub => {
      const subMerchantId = sub.merchant_id.toString();
      return subMerchantId === merchantId;
    });
  };

  const getVariationsToShow = (merchant) => {
    const userPlan = getUserPlan(merchant);
    const userPlanId = userPlan ? userPlan.plan_id.toString() : null;
    
    const sortedVariations = [...merchant.variation].sort((a, b) => {
      const aPlanId = a.plan_id.toString();
      const bPlanId = b.plan_id.toString();
      
      if (aPlanId === userPlanId) return -1;
      if (bPlanId === userPlanId) return 1;
      return 0;
    });
    
    const merchantId = merchant.merchant_id.toString();
    
    if (expandedMerchants[merchantId]) {
      return sortedVariations;
    }
    
    if (userPlan) {
      return sortedVariations.filter(variation => {
        const variationPlanId = variation.plan_id.toString();
        return variationPlanId === userPlanId;
      });
    }
    
    return [sortedVariations[0]];
  };

  const hasAdditionalPlans = (merchant) => {
    const userPlan = getUserPlan(merchant);
    return userPlan ? merchant.variation.length > 1 : merchant.variation.length > 1;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="h-full w-full">
      <div className="flex items-center justify-end mb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setViewMode("absolute")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              viewMode === "absolute" 
                ? 'bg-[#253985] text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            €
          </button>
          <button
            onClick={() => setViewMode("percentage")}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              viewMode === "percentage" 
                ? 'bg-[#253985] text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            %
          </button>
        </div>
      </div>

      {priceData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>{t('priceRadar.noData', 'No price change data available')}</p>
          <p className="text-sm mt-1">
            {t('priceRadar.willNotify', 'We\'ll notify you when subscription prices change')}
          </p>
        </div>
      ) : (
        <div className="space-y-4 max-h-80 overflow-y-auto">
          {priceData.map((merchant) => {
            const merchantId = merchant.merchant_id.toString();
            const variationsToShow = getVariationsToShow(merchant);
            const userPlan = getUserPlan(merchant);
            const isExpanded = expandedMerchants[merchantId];
            const hasMorePlans = hasAdditionalPlans(merchant);
            
            return (
              <div key={merchantId} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    {merchant.logo ? (
                      <img 
                        src={merchant.logo} 
                        alt={merchant.merchant_name} 
                        className="h-6 w-6 rounded-full object-cover mr-2"
                      />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                        <span className="text-xs font-medium text-gray-600">
                          {merchant.merchant_name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <h4 className="font-medium text-gray-800 text-sm">{merchant.merchant_name}</h4>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {hasMorePlans && (
                      <button 
                        onClick={() => toggleMerchantExpansion(merchantId)}
                        className="text-xs text-blue-500 flex items-center hover:text-blue-700 transition-colors"
                      >
                        {isExpanded ? 
                          t('priceRadar.showLess', 'Show less') : 
                          t('priceRadar.seeOtherPlans', 'See other plans')}
                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3 ml-1" />
                        ) : (
                          <ChevronDown className="h-3 w-3 ml-1" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2">
                  {variationsToShow.map((variation, idx) => {
                    const increase = ((variation.new_price - variation.old_price) / variation.old_price) * 100;
                    const absoluteChange = variation.new_price - variation.old_price;
                    
                    const isUserPlan = userPlan && 
                      variation.plan_id.toString() === userPlan.plan_id.toString();
                    
                    return (
                      <div key={idx} className={`flex justify-between items-center text-xs p-2 rounded-lg ${isUserPlan ? 'bg-blue-50 border border-blue-100' : ''}`}>
                        <div className="flex-1">
                          <div className="flex items-center">
                            <p className="font-medium text-gray-800">{variation.plan_name}</p>
                            {isUserPlan && (
                              <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded-full">
                                {t('priceRadar.yourPlan', 'Your plan')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center mt-1">
                            <span className="text-gray-500 line-through mr-1">
                              {formatCurrency(variation.old_price)}
                            </span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(variation.new_price)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <div className={`text-right ${increase >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            <p className="font-medium text-xs">
                              {viewMode === "absolute" 
                                ? `${increase >= 0 ? '+' : ''}${formatCurrency(absoluteChange)}`
                                : `${increase >= 0 ? '+' : ''}${increase.toFixed(1)}%`
                              }
                            </p>
                            <div className="flex items-center text-xs text-gray-500">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDate(variation.effective_date)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PriceRadar;